import React, { useCallback, useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { ResponsiveContainer, ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Loader2 } from "lucide-react";

/**
 * 🆕 22/07/2026 — Timeline cash-flow provvigionale (attesi vs incassati)
 * Grafico area sovrapposto a barre giornaliere · selettore 30/90 giorni.
 */

const formatDayShort = (iso) => {
  if (!iso) return "";
  const [, m, dd] = iso.split("-");
  return `${dd}/${m}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const exp = payload.find((p) => p.dataKey === "expected")?.value || 0;
  const col = payload.find((p) => p.dataKey === "collected")?.value || 0;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-lg p-2 text-xs">
      <div className="font-semibold text-slate-900 mb-1">{label}</div>
      <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-400" />Attesi: <strong className="tabular-nums">{formatCurrency(exp)}</strong></div>
      <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" />Incassati: <strong className="tabular-nums">{formatCurrency(col)}</strong></div>
    </div>
  );
};

export function CashflowTimelineWidget() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/dashboard/cashflow-timeline?days=${days}`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const chartData = (data?.series || []).map((s) => ({ ...s, dayLabel: formatDayShort(s.date) }));

  return (
    <div
      data-testid="cashflow-timeline-widget"
      className="bg-white border border-slate-200 rounded-xl p-4"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">Cash-flow provvigionale</p>
            <h3 className="font-display text-base font-semibold text-slate-900">Incassato vs Atteso · ultimi {days} giorni</h3>
            {data && (
              <p className="text-xs text-slate-600 mt-0.5">
                <span className="text-amber-700">Atteso <strong className="tabular-nums">{formatCurrency(data.total_expected)}</strong></span>
                <span className="mx-2 text-slate-300">·</span>
                <span className="text-emerald-700">Incassato <strong className="tabular-nums">{formatCurrency(data.total_collected)}</strong></span>
                {data.conversion_rate != null && (
                  <>
                    <span className="mx-2 text-slate-300">·</span>
                    <span className="text-indigo-700">Conversione <strong className="tabular-nums">{data.conversion_rate}%</strong></span>
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs" role="tablist">
          {[30, 90].map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`cashflow-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded transition-colors ${days === d ? "bg-white shadow-sm text-emerald-800 font-semibold" : "text-slate-500 hover:text-slate-800"}`}
            >
              {d}gg
            </button>
          ))}
        </div>
      </div>

      <div className="h-52" data-testid="cashflow-timeline-chart">
        {loading ? (
          <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm">Nessun dato nel periodo.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="expArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="dayLabel" tick={{ fontSize: 10, fill: "#64748b" }} interval={days > 30 ? Math.floor(days / 15) : 2} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={50} tickFormatter={(v) => `€${Math.round(v)}`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="expected" name="Attesi" stroke="#f59e0b" strokeWidth={2} fill="url(#expArea)" />
              <Bar dataKey="collected" name="Incassati" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={16} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
